"use client"

import React, { useEffect } from 'react'

interface ModalVideoProps {
  channel?: string
  isOpen: boolean
  videoId: string
  onClose: () => void
  autoplay?: boolean
  allowFullScreen?: boolean
  animationSpeed?: number
  classNames?: {
    modalVideo?: string
    modalVideoClose?: string
    modalVideoBody?: string
    modalVideoInner?: string
    modalVideoIframeWrap?: string
    modalVideoCloseBtn?: string
  }
}

export default function ModalVideo({
  channel = 'youtube',
  isOpen,
  videoId,
  onClose,
  autoplay = false,
  allowFullScreen = true,
  classNames,
}: ModalVideoProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const getVideoUrl = () => {
    if (channel === 'youtube') {
      return `https://www.youtube.com/embed/${videoId}${autoplay ? '?autoplay=1' : ''}`
    } else if (channel === 'vimeo') {
      return `https://player.vimeo.com/video/${videoId}${autoplay ? '?autoplay=1' : ''}`
    }
    return ''
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors cursor-pointer"
          aria-label="Close video"
        >
          <svg
            className="w-6 h-6"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
        <div className="relative pb-[56.25%] h-0">
          <iframe
            src={getVideoUrl()}
            allowFullScreen={allowFullScreen}
            className="absolute top-0 left-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          />
        </div>
      </div>
    </div>
  )
}
